import {useCallback} from 'react';
import * as THREE from 'three';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {
    Batch,
    PreviewFormValues,
    PreviewLocation,
} from 'components/customer/Preview3D/types';
import {useAppSelector} from 'store/customer';
import {selectData} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getEdge} from 'components/customer/Materials/store/selectors/edgeSelector';
import {Material, MaterialType} from 'components/customer/Materials/entity';
import {shallowEqual} from 'react-redux';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {RoomPlannerProduct} from 'components/customer/RoomPlanner/types';
import {
    detectShakerDoor,
    getExistingDoorsFromTemplate,
} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {
    addShakerDoorForDrawers,
    addShakerDoorDefault,
    applyShakerDoorMaterialsDefault,
} from 'components/customer/Preview3DCommon/3d-models/shaker';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {cloneDeep} from 'lodash';
import {PRODUCT_ID} from 'shared/constants/Products';

type ShakerDoorGroup = THREE.Group<THREE.Object3DEventMap>;

type ShakerDoorFactory = (opts: {
    variables: TemplateVariable;
    isRightDoor?: boolean;
    isPairDoor?: boolean;
    isCornerLeftDoor?: boolean;
    isCornerRightDoor?: boolean;
    isCornerPairDoor?: boolean;
    isTopHangDoor?: boolean;
    isLeftApplianceMainDoor?: boolean;
    isLeftApplianceExtendedDoor?: boolean;
    isRightApplianceMainDoor?: boolean;
    isRightApplianceExtendedDoor?: boolean;
    isDoorPullout?: boolean;
}) => Promise<ShakerDoorGroup | null>;

// Checks for 0 and "" values in drawer_face_height for the first array element.
// This was caused by a backend bug.
export const checkHasDrawers = (
    drawers: Array<{drawer_face_height?: number | string | null}> | undefined,
    drawerAmount?: number
): boolean =>
    Boolean(drawers?.length || Number(drawerAmount)) &&
    !!drawers?.[0]?.drawer_face_height;

const buildShakerDoorPromises = ({
    doorFlags,
    variables,
    createDoor,
    drawerDoorsPromise,
}: {
    doorFlags: ReturnType<typeof getExistingDoorsFromTemplate>;
    variables: TemplateVariable;
    createDoor: ShakerDoorFactory;
    drawerDoorsPromise: Promise<ShakerDoorGroup | null> | null;
}): Promise<ShakerDoorGroup[]> => {
    const {
        hasRightDoor,
        isPairDoor,
        isCornerPairDoor,
        hasCornerLeftDoor,
        hasCornerRightDoor,
        hasTopHangDoor,
        hasLeftApplianceMainDoor,
        hasLeftApplianceExtendedDoor,
        hasRightApplianceExtendedDoor,
        hasRightApplianceMainDoor,
        hasBiFoldLeftDoor,
        hasBiFoldRightDoor,
        hasLeftDoor,
        hasAnyDoors,
        hasDoorPullout,
        hasSplitDoor,
    } = doorFlags;

    const cornerPairDoorLeft = isCornerPairDoor && hasCornerLeftDoor;
    const cornerPairDoorRight = isCornerPairDoor && hasCornerRightDoor;

    if (
        hasBiFoldLeftDoor ||
        hasBiFoldRightDoor ||
        hasTopHangDoor ||
        hasSplitDoor
    ) {
        if (drawerDoorsPromise != null) {
            return drawerDoorsPromise.then((door) => (door ? [door] : []));
        }
        return Promise.resolve<ShakerDoorGroup[]>([]);
    }

    const promises: Promise<ShakerDoorGroup | null>[] = [];

    // Pull-out fronts (IsDoorPullout) look like a single full door but slide
    // out like a drawer instead of swinging open.
    if (hasDoorPullout) {
        promises.push(
            createDoor({
                variables,
                isRightDoor: false,
                isPairDoor: false,
                isDoorPullout: true,
            })
        );
    }

    if (isPairDoor || isCornerPairDoor) {
        promises.push(
            createDoor({
                variables,
                isRightDoor: false,
                isPairDoor: true,
                isCornerLeftDoor: cornerPairDoorLeft,
                isCornerRightDoor: false,
                isCornerPairDoor,
            })
        );
        promises.push(
            createDoor({
                variables,
                isRightDoor: true,
                isPairDoor: true,
                isCornerLeftDoor: false,
                isCornerRightDoor: cornerPairDoorRight,
                isCornerPairDoor,
            })
        );
    } else if (hasAnyDoors) {
        const hasLeftAppliance =
            hasLeftApplianceMainDoor && hasLeftApplianceExtendedDoor;
        const hasRightAppliance =
            hasRightApplianceExtendedDoor && hasRightApplianceMainDoor;

        if (hasLeftAppliance || hasRightAppliance) {
            const baseConfig = {
                variables,
                isRightDoor: false,
                isPairDoor: false,
                isCornerPairDoor,
                isTopHangDoor: false,
            };

            const doorConfigs = [];

            if (hasLeftAppliance) {
                // Core Left Appliance Doors
                doorConfigs.push(
                    {
                        ...baseConfig,
                        isCornerLeftDoor: cornerPairDoorLeft,
                        isCornerRightDoor: false,
                        isLeftApplianceMainDoor: true,
                    },
                    {
                        ...baseConfig,
                        isCornerLeftDoor: false,
                        isCornerRightDoor: cornerPairDoorRight,
                        isLeftApplianceExtendedDoor: true,
                    }
                );
                // Conditional Right Appliance Doors when Left Appliance is dominant
                if (hasRightAppliance || hasRightDoor) {
                    doorConfigs.push({
                        ...baseConfig,
                        isCornerLeftDoor: cornerPairDoorLeft,
                        isCornerRightDoor: false,
                        isLeftApplianceMainDoor: false,
                        isRightApplianceMainDoor: true,
                    });
                }
                if (hasRightAppliance) {
                    doorConfigs.push({
                        ...baseConfig,
                        isCornerLeftDoor: false,
                        isCornerRightDoor: cornerPairDoorRight,
                        isLeftApplianceExtendedDoor: false,
                        isRightApplianceExtendedDoor: true,
                    });
                }
            } else if (hasRightAppliance) {
                // Core Right Appliance Doors
                doorConfigs.push(
                    {
                        ...baseConfig,
                        isCornerLeftDoor: cornerPairDoorLeft,
                        isCornerRightDoor: false,
                        isLeftApplianceMainDoor: false,
                        isRightApplianceMainDoor: true,
                    },
                    {
                        ...baseConfig,
                        isCornerLeftDoor: false,
                        isCornerRightDoor: cornerPairDoorRight,
                        isLeftApplianceExtendedDoor: false,
                        isRightApplianceExtendedDoor: true,
                    }
                );
                // Conditional Left Appliance Door when Right Appliance is dominant
                if (hasLeftDoor) {
                    doorConfigs.push({
                        ...baseConfig,
                        isCornerLeftDoor: cornerPairDoorLeft,
                        isCornerRightDoor: false,
                        isLeftApplianceMainDoor: true,
                    });
                }
            }

            // Map configs to createDoor promises and push them all at once
            promises.push(...doorConfigs.map((config) => createDoor(config)));
        } else {
            // Default fallback door configuration
            promises.push(
                createDoor({
                    variables,
                    isRightDoor: hasRightDoor,
                    isPairDoor: false,
                    isCornerLeftDoor: hasCornerLeftDoor,
                    isCornerRightDoor: hasCornerRightDoor,
                    isCornerPairDoor: false,
                    isTopHangDoor: hasTopHangDoor,
                })
            );
        }
    }

    if (drawerDoorsPromise != null) promises.push(drawerDoorsPromise);

    return Promise.all(promises).then((results) =>
        results.filter((door): door is ShakerDoorGroup => door !== null)
    );
};

export const addShakerDoorsToRoomPlannerCabinet = async (
    cabinetValues: RoomPlannerProduct,
    variables: TemplateVariable,
    batch: Batch,
    selectedExteriorMaterial?: Material | null,
    showTexture?: boolean,
    door?: ReturnType<typeof getDoor> | null
): Promise<THREE.Group[]> => {
    if (
        !selectedExteriorMaterial ||
        !detectShakerDoor(selectedExteriorMaterial, door).isShakerDoor
    ) {
        return [];
    }

    const doorFlags = getExistingDoorsFromTemplate(batch);
    const {hasAnyDoors, isCornerPairDoor, hasDoorPullout} = doorFlags;
    const hasDrawers = checkHasDrawers(cabinetValues?.drawers);

    if (
        (!hasAnyDoors && !isCornerPairDoor && !hasDrawers && !hasDoorPullout) ||
        variables.isHighSwing ||
        variables.isHighLift
    ) {
        return [];
    }

    const carcaseThickness = cabinetValues.cabinet_carc_thickness || 16.5;
    const exteriorThickness = cabinetValues.cabinet_ext_thickness || 16.5;

    // Pair / appliance doors honour the cabinet's saved open state; the
    // primary single door always starts closed (matches original behaviour).
    const createDoor: ShakerDoorFactory = ({
        variables: v,
        isRightDoor = false,
        isPairDoor = false,
        isCornerLeftDoor = false,
        isCornerRightDoor = false,
        isCornerPairDoor = false,
        isTopHangDoor = false,
        isLeftApplianceMainDoor = false,
        isLeftApplianceExtendedDoor = false,
        isRightApplianceMainDoor = false,
        isRightApplianceExtendedDoor = false,
        isDoorPullout = false,
    }) =>
        addShakerDoorDefault({
            values: cabinetValues as unknown as PreviewFormValues,
            variables: v,
            isRightDoor,
            isPairDoor,
            isCornerLeftDoor,
            isCornerRightDoor,
            isCornerPairDoor,
            isTopHangDoor,
            isLeftApplianceMainDoor,
            isLeftApplianceExtendedDoor,
            isRightApplianceMainDoor,
            isRightApplianceExtendedDoor,
            isDoorPullout,
            carcaseThickness,
            exteriorThickness,
            showTexture,
            initiallyOpen:
                isPairDoor ||
                isLeftApplianceMainDoor ||
                isLeftApplianceExtendedDoor ||
                isRightApplianceMainDoor ||
                isRightApplianceExtendedDoor ||
                isDoorPullout
                    ? (cabinetValues.isDoorOrDrawerOpen ?? false)
                    : false,
            origin: 'ROOM_PLANNER',
        });
    let drawerDoorsPromise: Promise<ShakerDoorGroup | null> | null = null;

    if (hasDrawers && !cabinetValues?.isInnerDrawer) {
        drawerDoorsPromise = addShakerDoorForDrawers({
            values: cabinetValues as unknown as PreviewFormValues,
            variables,
            carcaseThickness,
            exteriorThickness,
            showTexture,
            origin: 'ROOM_PLANNER',
            initiallyOpen: cabinetValues.isDoorOrDrawerOpen,
            hasAnyDoors,
        });

        if (!hasAnyDoors && !hasDoorPullout) {
            return drawerDoorsPromise.then((door) => (door ? [door] : []));
        }
    }

    return buildShakerDoorPromises({
        doorFlags,
        variables,
        createDoor,
        drawerDoorsPromise,
    });
};

const useShakerDoors = (
    showTexture = false,
    previewLocation: PreviewLocation
) => {
    const {values, productDataStore} = use3DContext();
    const {carcaseThickness, exteriorThickness} = useMaterialTexture();

    const batchData = useAppSelector(selectData) as Batch | null;

    const selectedExteriorMaterial = useAppSelector(
        (state) => getMaterial(state, MaterialType.EXTERIOR),
        shallowEqual
    );
    const selectedExteriorMaterialEdge = useAppSelector(
        (state) => getEdge(state, MaterialType.EXTERIOR),
        shallowEqual
    );

    const horizontalGrainExt = Boolean(values.hor_grain_ext);

    const applyShakerDoorMaterials = useCallback(
        (model: THREE.Group, showTexture: boolean) => {
            applyShakerDoorMaterialsDefault(
                model,
                showTexture,
                selectedExteriorMaterial?.image
            );
        },
        [
            selectedExteriorMaterial,
            selectedExteriorMaterialEdge,
            horizontalGrainExt,
            showTexture,
        ]
    );

    const addShakerDoorToScene = useCallback(
        ({
            variables,
            isRightDoor = false,
            isPairDoor = false,
            isCornerLeftDoor = false,
            isCornerRightDoor = false,
            isCornerPairDoor = false,
            isTopHangDoor = false,
            isLeftApplianceMainDoor = false,
            isLeftApplianceExtendedDoor = false,
            isRightApplianceMainDoor = false,
            isRightApplianceExtendedDoor = false,
            isDoorPullout = false,
        }: {
            variables: TemplateVariable;
            isRightDoor?: boolean;
            isPairDoor?: boolean;
            isCornerLeftDoor?: boolean;
            isCornerRightDoor?: boolean;
            isCornerPairDoor?: boolean;
            isTopHangDoor?: boolean;
            isLeftApplianceMainDoor?: boolean;
            isLeftApplianceExtendedDoor?: boolean;
            isRightApplianceMainDoor?: boolean;
            isRightApplianceExtendedDoor?: boolean;
            isDoorPullout?: boolean;
            isBiFoldDoor?: boolean;
            hasBiFoldLeftDoor?: boolean;
            hasBiFoldRightDoor?: boolean;
        }): Promise<ShakerDoorGroup | null> => {
            return addShakerDoorDefault({
                values,
                variables,
                isRightDoor,
                isPairDoor,
                carcaseThickness,
                exteriorThickness,
                showTexture,
                initiallyOpen: previewLocation === 'DOORS' ? false : true,
                applyShakerDoorMaterials,
                isCornerLeftDoor,
                isCornerRightDoor,
                isCornerPairDoor,
                isTopHangDoor,
                isLeftApplianceMainDoor,
                isLeftApplianceExtendedDoor,
                isRightApplianceMainDoor,
                isRightApplianceExtendedDoor,
                isDoorPullout,
            });
        },
        [
            values,
            carcaseThickness,
            exteriorThickness,
            applyShakerDoorMaterials,
            showTexture,
            previewLocation,
        ]
    );

    const addShakerDoorForDrawersToScene = useCallback(
        async (
            variables: TemplateVariable,
            hasAnyDoors: boolean
        ): Promise<ShakerDoorGroup | null> => {
            const drawerValues = cloneDeep(values);
            const isDrawerComponent =
                Number(drawerValues.cabinet_type) ===
                PRODUCT_ID.DRAWER_COMPONENT;
            if (isDrawerComponent) {
                drawerValues.drawers = (
                    drawerValues?.drawer_face_height as unknown as number[]
                )?.map((faceHeight) => ({
                    drawer_face_height: faceHeight,
                    drawer_runner_specs: '',
                    drawer_type: 1,
                }));
                drawerValues.cabinet_width = drawerValues.cabinet_panel_width;
                drawerValues.cabinet_depth = -exteriorThickness;
                drawerValues.cabinet_height = drawerValues.cabinet_panel_length;
                drawerValues.cabinet_total_drawer_height =
                    drawerValues.cabinet_panel_length;
            }
            const drawersGroupPromise = addShakerDoorForDrawers({
                values: drawerValues,
                variables,
                carcaseThickness,
                exteriorThickness,
                showTexture,
                applyShakerDoorMaterials,
                initiallyOpen:
                    previewLocation === 'DRAWERS' || isDrawerComponent
                        ? false
                        : true,
                hasAnyDoors,
            });
            const cabinetGroup = await drawersGroupPromise;
            cabinetGroup.position.z -= drawerValues.cabinet_height / 2;
            return drawersGroupPromise;
        },
        [
            values,
            carcaseThickness,
            exteriorThickness,
            applyShakerDoorMaterials,
            showTexture,
            previewLocation,
        ]
    );

    const addShakerDoorWithAutoPositioning = useCallback(
        (variables: TemplateVariable): Promise<ShakerDoorGroup[]> => {
            const doorFlags = getExistingDoorsFromTemplate(batchData);
            const {hasAnyDoors, hasDoorPullout} = doorFlags;
            const isDrawerComponent =
                Number(values?.cabinet_type) === PRODUCT_ID.DRAWER_COMPONENT;

            const drawers = values?.drawers;
            const hasDrawers =
                checkHasDrawers(drawers, values?.drawer_amount) ||
                (isDrawerComponent && values?.drawer_amount);

            if (
                (!hasAnyDoors && !hasDrawers && !hasDoorPullout) ||
                variables.isHighSwing ||
                variables.isHighLift
            ) {
                return Promise.resolve<ShakerDoorGroup[]>([]);
            }

            let drawerDoorsPromise: Promise<ShakerDoorGroup | null> | null =
                null;

            const isCustomInnerDrawers =
                !!drawers?.length &&
                !!Number(values?.drawer_amount) &&
                !isDrawerComponent;

            if (hasDrawers && !isCustomInnerDrawers) {
                drawerDoorsPromise = addShakerDoorForDrawersToScene(
                    variables,
                    hasAnyDoors
                );

                if (!hasAnyDoors && !hasDoorPullout) {
                    return drawerDoorsPromise.then((door) =>
                        door ? [door] : []
                    );
                }
            }

            return buildShakerDoorPromises({
                doorFlags,
                variables,
                createDoor: addShakerDoorToScene,
                drawerDoorsPromise,
            });
        },
        [
            addShakerDoorToScene,
            addShakerDoorForDrawersToScene,
            values,
            productDataStore,
            carcaseThickness,
            exteriorThickness,
            batchData,
        ]
    );

    return {
        addShakerDoorToScene,
        addShakerDoorWithAutoPositioning,
    };
};

export default useShakerDoors;
